import React, { useState } from 'react'
import styled from 'styled-components'
import { identity } from 'lodash'
import { Box } from 'ui-components'
import { Select, ItemRenderer, ItemPredicate } from '@blueprintjs/select'
import {
  Classes,
  MenuItem,
  Button,
  ControlGroup,
  Spinner,
} from '@blueprintjs/core'
import { RejectReason } from 'ui-io/task-io'
import { useQuery } from 'react-query'
import assetsSettingsQuery from 'ui-io/queries/assets/assets-settings-query'
import { TaskRejectReasonsFieldT } from 'ui-io/queries/assets/asset-mutation'
import { useEffect } from 'react'

const RejectionReasonInput = ({
  reason = { reason: '', field: '' },
  onChange,
  onDelete,
}: {
  reason?: RejectReason
  onChange: (reason: RejectReason) => void
  onDelete?: () => void
}) => {
  const [reasons, setReasons] = useState<string[]>([])
  const [rejectReasonsConfig, setRejectReasonsConfig] = useState<
    TaskRejectReasonsFieldT[]
  >([])
  const rejectReasonsConfigConnection = useQuery(
    assetsSettingsQuery(
      '/settings/task',
      'task-reject-reasons-config',
    ),
  )
  useEffect(() => {
    if (
      rejectReasonsConfigConnection.data?.settings?.keys[
        'task-reject-reasons-config'
      ]?.config
    ) {
      setRejectReasonsConfig(
        rejectReasonsConfigConnection.data?.settings?.keys[
          'task-reject-reasons-config'
        ]?.config,
      )
    }
  }, [rejectReasonsConfigConnection])

  const onFieldSelect = (field: string) => {
    if (field) {
      const selectedField = rejectReasonsConfig.find(
        ({ name }) => name === field,
      )
      if (selectedField) {
        setReasons(selectedField.options)
      }
      onChange({ ...reason, field })
    }
  }

  return (
    <Box>
      {rejectReasonsConfigConnection.isLoading ? (
        <Spinner></Spinner>
      ) : (
        <ControlGroup fill={true}>
          <FilledSelect
            activeItem={reason.field}
            onItemSelect={onFieldSelect}
            items={rejectReasonsConfig.map(({ name }) => name)}
            createNewItemFromQuery={identity}
            itemPredicate={itemPredicate}
            itemRenderer={itemRenderer}
          >
            <Button
              fill
              alignText="left"
              rightIcon="caret-down"
              text={reason.field || 'Field'}
            />
          </FilledSelect>
          <FilledSelect
            disabled={!reasons.length}
            activeItem={reason.reason}
            onItemSelect={(r) => {
              if (r) {
                onChange({ ...reason, reason: r })
              }
            }}
            items={reasons}
            createNewItemFromQuery={identity}
            itemPredicate={itemPredicate}
            itemRenderer={itemRenderer}
          >
            <Button
              fill
              alignText="left"
              rightIcon="caret-down"
              text={reason.reason || 'Reason'}
            />
          </FilledSelect>
          <Button
            minimal
            icon="remove"
            onClick={onDelete}
            disabled={!onDelete}
          />
        </ControlGroup>
      )}
    </Box>
  )
}

const FilledSelect = styled(Select.ofType<string>())`
  .${Classes.POPOVER_TARGET} {
    width: 210px;
  }
`

const itemPredicate: ItemPredicate<string> = (query, str) => str.includes(query)

const itemRenderer: ItemRenderer<string> = (
  string,
  { handleClick, modifiers },
) => {
  if (!modifiers.matchesPredicate) {
    return null
  }
  return (
    <MenuItem
      active={modifiers.active}
      disabled={modifiers.disabled}
      text={string}
      key={string}
      onClick={handleClick}
    />
  )
}

export default RejectionReasonInput
